import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useStore } from 'store/store';
import {
  GetWalletsByAssetResponse,
  OpenSubAccountRequest,
  useUtxExchangeSubAccountOpenSubAccount,
  UtxExchangeSubAccountOpenSubAccountMutationError,
  UtxExchangeSubAccountOpenSubAccountMutationResult
} from '../../../../../packages/utx-client/src/react';

export interface UseExchangeSubAccountProp {
  general: {
    wallets: Array<GetWalletsByAssetResponse>;
    walletId: string;
    walletLabel: string;
    alertMessage: string;
    balance: string;
    assetName: string;
    setWalletId: (input: string) => void;
    setAssetName: (input: string) => void;
    getAccountType: (input: string) => string;
    setAlertMessage: (input: string) => void;
  };
  postOpenAccount: {
    postOpenAccountApi: (
      request: OpenSubAccountRequest,
      onSuccess: (data: UtxExchangeSubAccountOpenSubAccountMutationResult) => void,
      onError: (error: UtxExchangeSubAccountOpenSubAccountMutationError) => void
    ) => void;
    isLoading: boolean;
  };
}

export const useExchangeSubAccount = (): UseExchangeSubAccountProp => {
  const store = useStore();
  const [walletId, setWalletId] = useState<string>('');
  const [walletLabel, setWalletLabel] = useState<string>('');
  const [alertMessage, setAlertMessage] = useState<string>('');
  const [assetName, setAssetName] = useState<string>('HKD');
  const wallets = store.wallets;

  const { t } = useTranslation('exchange-sub-account');

  useEffect(() => {
    if (walletId && wallets) {
      const walletsWithAsset = wallets.filter((wallet) => wallet.asset === assetName)?.[0];
      if (!walletsWithAsset) return [];
      let index = walletsWithAsset.wallets.findIndex((item) => item.id === walletId);
      if (index != -1) {
        setWalletLabel(
          walletsWithAsset.wallets[index].accountLabel
            ? walletsWithAsset.wallets[index].accountLabel
            : walletsWithAsset.wallets[index].accountName
        );
      }
    }
  }, [walletId]);

  const balance = useMemo(() => {
    if (walletId && wallets) {
      const walletsWithAsset = wallets.filter((wallet) => wallet.asset === assetName)?.[0];
      if (!walletsWithAsset) return '0';
      let index = walletsWithAsset.wallets.findIndex((item) => item.id === walletId);
      if (index != -1) {
        return walletsWithAsset.wallets[index].balance;
      }
    }
    return '0';
  }, [walletId]);

  /**
   * Post open account
   */
  const openAccount = useUtxExchangeSubAccountOpenSubAccount();
  const postOpenAccountApi = (
    request: OpenSubAccountRequest,
    onSuccess: (data: UtxExchangeSubAccountOpenSubAccountMutationResult) => void,
    onError: (error: UtxExchangeSubAccountOpenSubAccountMutationError) => void
  ) => {
    openAccount.mutate(
      {
        data: request
      },
      {
        onSuccess,
        onError
      },
      {
        axios: {
          headers: {
            Authorization: `Bearer ${store.authToken}`
          }
        }
      }
    );
  };

  const isLoadingPostOpenAccount = useMemo(() => {
    return openAccount.isLoading;
  }, [openAccount.isLoading]);

  const getAccountType = (input: string): string => {
    const value = input ? input : 'Other Assets';
    switch (value) {
      case 'wallet_jurisdiction_HK':
        return t('exchange-account::glossary.wallet_jurisdiction_HK');
      case 'wallet_jurisdiction_US':
        return t('exchange-account::glossary.wallet_jurisdiction_US');
      case 'wallet_jurisdiction_SG':
        return t('exchange-account::glossary.wallet_jurisdiction_SG');
      case 'wallet_digital':
        return t('exchange-account::glossary.wallet_digital');
      case 'wallet_commodities':
        return t('exchange-account::glossary.wallet_commodities');
      case 'commission':
        return t('exchange-account::glossary.commission');
      case 'operation':
      case 'operating':
        return t('exchange-account::glossary.operation');
      case 'card_statement':
        return t('exchange-account::glossary.card_statement');
      case 'consolidated':
        return t('exchange-account::glossary.consolidated');
      case 'consolidated_offshore':
        return t('exchange-account::glossary.consolidated_offshore');
      default:
        return value;
    }
  };

  return {
    general: {
      wallets,
      walletId,
      walletLabel,
      alertMessage,
      balance,
      assetName,
      setWalletId,
      setAssetName,
      getAccountType,
      setAlertMessage
    },
    postOpenAccount: {
      postOpenAccountApi,
      isLoading: isLoadingPostOpenAccount
    }
  };
};
